import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { Hold } from '../../../types/climb';
import { useZoomPan } from '../board-grid/useZoomPan';
import {
  canMovePhotoStep,
  movePhotoStep,
  orderPhotoHolds,
  photoHoldNumbers,
  photoStepNeighbour,
} from '../photo-circuit';
import { grownOutline, holdArea, holdOutline, pointInOutline } from '../hold-outline';
import {
  FALLBACK_PHOTO_HOLD_ROLE,
  INITIAL_PHOTO_HOLD_ROLE,
  PHOTO_HOLD_ROLE_COLORS,
  PHOTO_HOLD_ROLE_CYCLE,
  circularHold,
  holdRadius,
  missingPhotoHoldRoles,
  photoHoldRole,
  photoRoleAtLimit,
  type PhotoHoldRole,
} from '../photo-hold-roles';

/**
 * The sizes a press steps through, as fractions of the photo's height.
 *
 * A handful of rungs, not a continuum: stepping by a ratio from any starting
 * size gives a dozen of them, so getting back to the one wanted means going
 * round the whole way, and no two circles on the wall end up the same size.
 * These five are what a wall actually has — a crimp, a small hold, a hold, a
 * jug, a volume.
 *
 * It opens on the smallest: most holds are small, and a circle that arrives too
 * big hides the hold underneath it, which is the one thing the setter is looking
 * at while placing it. The bottom rung is the one that has to fit a crimp on a
 * photo of a whole wall, which is smaller than it sounds.
 */
export const HOLD_SIZE_LADDER: readonly number[] = [0.03, 0.04, 0.055, 0.075, 0.1];

/** Circle radius as a fraction of the photo's height, and the range it may take. */
export const DEFAULT_HOLD_RADIUS: number = HOLD_SIZE_LADDER[0];
// The handles are free of the ladder and size a circle to whatever the hold
// wants; only the range is theirs.
export const MIN_HOLD_RADIUS = 0.012;
export const MAX_HOLD_RADIUS = 0.18;

/** How far outside a circle a press still counts as being on it, on screen. */
const HOLD_HIT_PX = 6;

/** How near a resize handle a press has to land to take hold of it, on screen. */
const HANDLE_HIT_PX = 10;

/** How far a press has to travel before it is a drag rather than a plain press. */
const DRAG_THRESHOLD_PX = 4;

/**
 * How many steps back the undo goes. A wall's worth of circles is a few dozen,
 * and a setter who wants to be further back than this wants the eraser.
 */
const MAX_HISTORY = 40;

/** Where the resize handles sit on the selected circle, as compass directions. */
export const HOLD_HANDLES = [
  { dx: 0, dy: -1 },
  { dx: 1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: -1, dy: 0 },
] as const;

function clampRadius(radius: number): number {
  return Math.min(MAX_HOLD_RADIUS, Math.max(MIN_HOLD_RADIUS, radius));
}

interface Drag {
  /** The circles the press has hold of — several, when a group is being moved. */
  indices: number[];
  /**
   * The one actually under the press. A press on a group keeps the group so it
   * can be dragged as one; released without travelling, it means "just this
   * one" instead, and that is the one it means.
   */
  pressed: number;
  /**
   * Whether that circle was already the whole selection when the press landed.
   * It is what tells a press that *chooses* a circle from one that acts on the
   * circle already chosen — and the second of those cycles its size.
   */
  wasOnlySelection: boolean;
  /** What the press is doing to them. */
  /**
   * `pinned` is a mark that was picked up but will not be moved by the drag —
   * a measured outline. It still goes through the drag because that is what
   * carries a second press on the mark already in hand through to the pointer
   * lifting, which is how a role is cycled.
   */
  mode: 'size' | 'move' | 'pinned';
  startX: number;
  startY: number;
  moved: boolean;
  /** Whether this drag has already put its starting state on the undo stack. */
  remembered?: boolean;
  /** Where the press landed, in fractions. */
  grabOffset: { x: number; y: number };
  /**
   * Where each held circle's centre sat when it was grabbed, in the order of
   * `indices`. A move is applied to *these* rather than to wherever the circles
   * have got to, so the travel is measured once from the press and cannot
   * accumulate as the pointer is dragged.
   */
  from: { x: number; y: number }[];
}

export interface PhotoHoldsInput {
  /** Natural size of the photo, from the <img> that renders it. */
  naturalWidth: number;
  naturalHeight: number;
  holds: Hold[];
  onChange: (holds: Hold[]) => void;
  /**
   * What a press on the circle already chosen does. The scans panel walks its
   * **role** — the wall's colour is what a scan most often gets wrong. The climb
   * editor walks its **size**: there the role was chosen from the toolbar a
   * moment ago and is right, while the size is what varies hold by hold.
   */
  repeatPressCycles?: 'role' | 'size';
  /**
   * Whether the circles are a **sequence** rather than a set: the climb is
   * climbed hold by hold in the order they are stored in, each wearing its
   * number. Everything that changes the drawing then settles that order — starts
   * first, top last — and the numbers follow from it (see `photo-circuit.ts`).
   */
  circuit?: boolean;
  /**
   * Whether a press on bare wall draws a new circle.
   *
   * Off on a scanned wall, where every hold is already a placeholder the
   * detector found and the climb is made by *choosing* them rather than by
   * drawing. Nothing else changes: the holds still pick up, repaint and go in
   * the bin, and the picture still pans under a drag.
   */
  drawing?: boolean;
  /**
   * Whether a drag across bare wall sweeps up every mark it covers.
   *
   * It is the gesture that takes the place of drawing on a scanned wall, and
   * the two cannot both have the drag: where circles are placed, a press on the
   * wall means a new one. Panning is not lost either way — the wheel pans, with
   * shift for sideways, and a pinch still works.
   */
  marquee?: boolean;
  /**
   * Turn a press into **membership** rather than into picking a mark up.
   *
   * On a scanned wall the marks are all already there, so the two things a
   * press could mean come apart: *is this hold part of the climb* and *which of
   * the climb's holds am I about to change*. A plain press answers the first —
   * it switches a placeholder on and off — and ⌘-press answers the second,
   * among the ones already switched on, which is what a group edit acts on.
   *
   * Absent on every other editor, where the marks only exist because somebody
   * drew them and a press can only mean the second thing.
   */
  activation?: { active: number[]; onChange: (next: number[]) => void };
  /**
   * Marks that are **on the wall but not in this climb**: the holds the scan
   * found and nobody has taken yet.
   *
   * They are drawn and they are pressable, and that is all — they are not part
   * of the drawing, so they are never saved, moved, repainted or counted. A
   * press on one calls `onTakeCandidate`, and it is the page's business what
   * that means (on the scans panel: it joins the climb at once).
   *
   * **Without `onTakeCandidate` the press means what a press on bare wall means
   * where circles are drawn**: a mark goes down there when the finger lifts, in
   * the role armed on the bar, and is picked up so the bar acts on it next —
   * only it is the placeholder's own shape rather than a new circle. That is the
   * member app's editor on a scanned wall. Placed on the lift and not on the
   * press for the reason a circle is: on a phone the same finger may be the
   * first of a pinch, or the start of the page being scrolled.
   *
   * **The climb's own holds are asked first.** A hold that has been taken sits
   * exactly where its placeholder was, so without that rule pressing a hold of
   * the climb would find the candidate underneath it instead.
   */
  candidates?: Hold[];
  onTakeCandidate?: (hold: Hold, index: number) => void;
  /**
   * Whether every mark stays exactly as it is: where it is, and the size it is.
   *
   * A measured outline always does — its shape came from the pixels of the
   * wall, so a thumb can only move it off its hold. This asks the same of every
   * circle, for a wall where each mark is one of the holds a scan found, taken
   * as it was found: a box the detector drew a circle round, dragged anywhere
   * else, is a hold the scan never found, which is exactly the circle drawn
   * where you like that such a wall has stopped offering.
   *
   * Picking up, repainting and the bin are untouched.
   */
  pinned?: boolean;
  /**
   * **A drag draws a rectangle on the photo instead of gathering marks**, and
   * the rectangle is handed here when the pointer lifts, in fractions of the
   * photo. Set only while armed: on the scans panel, the area a setter wants
   * looked at again for holds the scan missed.
   *
   * Armed, the drag starts the rectangle whatever it lands on — a missed hold
   * sits among found ones, and a press on one of those must not take it.
   */
  onSweepArea?: (area: { x: number; y: number; width: number; height: number }) => void;
}

/**
 * Smaller than this share of the photo on either side, a swept area is a press
 * that wobbled rather than an area.
 */
const MIN_SWEPT_AREA = 0.01;

export type PhotoHoldsController = ReturnType<typeof usePhotoHolds>;

/**
 * Circling the holds of a climb on its photo: the state, the gestures, and what
 * the controls act on — held apart from where either is drawn.
 *
 * The setter picks a role and paints with it; the role stays selected between
 * presses, the same paint model as the board grid, which is where these five
 * roles and their icons come from. A press on the bare wall drops a circle the
 * size of the last one sized — holds come in runs, so that is nearly always the
 * size wanted. A press on an existing circle — anywhere on it, not just its
 * edge — selects it, and dragging from there moves it. A singly selected circle
 * wears four handles, and dragging one is the only way a circle is resized.
 *
 * One finger is the pen here, so it never pans: the picture is moved with two,
 * which also pinch it (see useZoomPan's `panWithOnePointer`). Presses are
 * hit-tested against the *content*, not the frame, so everything lands where the
 * finger is however far the photo is zoomed in.
 *
 * Circles are stored as fractions of the photo's natural size, which is the
 * frame of reference every renderer draws them back in.
 */
export function usePhotoHolds({
  naturalWidth,
  naturalHeight,
  holds,
  onChange,
  repeatPressCycles = 'size',
  circuit = false,
  drawing = true,
  marquee = false,
  activation,
  candidates,
  onTakeCandidate,
  pinned = false,
  onSweepArea,
}: PhotoHoldsInput) {
  const zoom = useZoomPan();
  const [activeRole, setActiveRole] = useState<PhotoHoldRole>(INITIAL_PHOTO_HOLD_ROLE);
  const [radius, setRadius] = useState(DEFAULT_HOLD_RADIUS);
  // Which circles are picked up, by index. More than one at a time so a run of
  // holds can be repainted, resized or removed in one go — a wall's colour is
  // usually got wrong for a handful of them together, not one by one.
  const [selected, setSelected] = useState<number[]>([]);

  // The circle a press has hold of, and what it is doing to it. A ref because
  // the move handler reads it as the pointer travels, not as it was set up.
  const dragRef = useRef<Drag | null>(null);

  // The rectangle being swept across the wall, in fractions of the photo, and
  // what was already held when it started — so a sweep with Cmd adds to a
  // selection instead of replacing it.
  const [band, setBand] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const bandRef = useRef<{ x: number; y: number; base: number[] } | null>(null);

  // Where a press on the bare wall landed, waiting to become a circle when the
  // finger lifts. It waits because the same first finger begins a pinch, and a
  // gesture that turns out to be two fingers moving the picture must not leave a
  // circle behind where the first one happened to touch down.
  const pendingRef = useRef<{
    x: number;
    y: number;
    /** Where the finger landed on screen, to tell a tap from a drag. */
    startX: number;
    startY: number;
    /**
     * The placeholder pressed, where the press was on one: it is what goes down
     * instead of a circle (see `candidates`).
     */
    mark?: Hold;
  } | null>(null);
  // The fingers currently down on the photo, and whether this gesture ever had
  // more than one. Counted here rather than read off `event.isPrimary`, which
  // not every source of pointer events fills in.
  const activePointers = useRef(new Set<number>());
  const multiTouchRef = useRef(false);

  // What the circles were before each thing that changed them.
  //
  // Undo steps back through *actions*, not circles: clearing the wall is one
  // action and comes back in one press, which "drop the last circle" could never
  // do — after a clear there was no last circle left to drop. A drag records
  // where it started rather than every pixel of its travel, so undoing a move
  // puts the hold back where it was picked up instead of a nudge behind.
  const [history, setHistory] = useState<Hold[][]>([]);
  const remember = useCallback(
    (before: Hold[]) => setHistory((past) => [...past.slice(1 - MAX_HISTORY), before]),
    []
  );

  // The brush is never moved on by itself. It used to step off a start once two
  // were down — a guess at what came next — but the setter now says what each
  // circle is *at* the circle, right after placing it (see HoldActions), so a
  // brush that had quietly changed underneath them meant the answer they were
  // shown was not the one they last gave.
  //
  // What is left is the cap: four starts, two tops (HOLD_ROLE_LIMITS). It greys
  // the colour out on the panel, and a circle drawn once it is full lands as a
  // plain hold rather than as a fifth start — a dead tap on the wall would be
  // the worse of the two, since nothing on screen would say why.

  /** The role wanted, or the plain hold it gives way to when it has no slot left. */
  const freeRole = (placed: Hold[], wanted: PhotoHoldRole): PhotoHoldRole =>
    photoRoleAtLimit(placed, wanted) ? FALLBACK_PHOTO_HOLD_ROLE : wanted;

  // A selection only means anything while the circles it points at are still
  // there — a shorter list (an undo, or another climb's marks) prunes it.
  useEffect(() => {
    setSelected((current) => {
      const kept = current.filter((index) => index < holds.length);
      return kept.length === current.length ? current : kept;
    });
  }, [holds.length]);

  const clearSelection = useCallback(() => setSelected([]), []);

  /**
   * The hold as a plain circle, having lost whatever shape it had.
   *
   * **A detector's outline is not hand-editable**, and this is where that is
   * enforced: dragging forty points on a phone is not a feature, so the handles
   * stay circle-only and the moment a setter moves or resizes a measured hold it
   * becomes the circle it already has underneath. Precision where it is free,
   * simplicity where it is not.
   *
   * Only moving and sizing. Repainting a circle is not reshaping it, so a hold
   * given another role keeps its shape.
   */
  function circleOf(hold: Hold, x: number, y: number, radius: number): Hold {
    const circle = {
      ...hold,
      ...circularHold(x, y, radius, hold.color, naturalWidth, naturalHeight),
    };
    delete circle.outline;

    return circle;
  }

  /**
   * Whether a mark's geometry is anybody's to change — where it is and how big.
   *
   * **Refused per mark, not per screen.** A measured outline was traced from the
   * pixels of the wall, so moving or sizing it can only take it off the hold it
   * came from (and sizing it would make it a circle, see `circleOf`); a circle
   * drawn by hand has no measurement behind it, and the two sit side by side.
   * `pinned` refuses it for every mark on the wall.
   */
  function fixedGeometry(hold: Hold | undefined): boolean {
    return pinned || Boolean(hold && holdOutline(hold));
  }

  /**
   * Hand the drawing on, having first settled the order where there is one to
   * settle: on a circuit a climb is climbed from its starts to its top, so the
   * two ends are put back at the ends whatever the change was (see
   * `orderPhotoHolds`) — repainting a circle in the middle green moves it to the
   * front of the run, and it does so as the setter watches.
   *
   * The selection follows the circles it was on rather than the places they were
   * in: everything here points at a hold by where it sits, so a reorder under a
   * chosen circle would leave the panel acting on somebody else's hold.
   *
   * Returns what was actually emitted, for the callers that then have to say
   * where the circle they just placed ended up.
   */
  function commit(next: Hold[]): Hold[] {
    if (!circuit) {
      onChange(next);
      return next;
    }

    const ordered = orderPhotoHolds(next);
    onChange(ordered);
    setSelected((current) =>
      current
        .map((index) => (next[index] ? ordered.indexOf(next[index]) : -1))
        .filter((index) => index >= 0)
    );

    return ordered;
  }

  /** The one circle picked up, or null when none or several are. */
  const onlySelected = selected.length === 1 ? selected[0] : null;

  /**
   * The circle the controls hang off — the last one picked up.
   *
   * A selection of several is acted on as one (repainted, removed), but it has
   * no one place on the wall, so the panel that acts on it stands at the circle
   * the setter reached for last: that is where they are looking.
   */
  const anchorHold = selected.length > 0 ? (holds[selected[selected.length - 1]] ?? null) : null;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') clearSelection();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clearSelection]);

  /**
   * While a press has hold of a circle, the page underneath stays where it is.
   *
   * `touch-action` cannot be what says so. The browser settles what a touch is
   * allowed to do when the finger lands and will not hear otherwise for the rest
   * of the gesture — so a photo that lets the page be scrolled with a finger
   * (which at rest it must, or the page could not be read past) has already
   * given a downward drag away by the time we know it was a hold being moved.
   * That is the "it moves a little, then the page scrolls" of it: the circle
   * follows until the browser's slop is used up, then the scroll takes over and
   * the pointer is cancelled out from under the drag.
   *
   * Refusing the move itself is what actually stops it, and it has to be a
   * native non-passive listener — React's are passive, and a passive listener's
   * preventDefault is ignored.
   */
  useEffect(() => {
    const frame = zoom.frame;
    if (!frame) return;

    function holdTheFrameStill(event: TouchEvent) {
      if (dragRef.current && event.cancelable) event.preventDefault();
    }

    frame.addEventListener('touchmove', holdTheFrameStill, { passive: false });
    return () => frame.removeEventListener('touchmove', holdTheFrameStill);
  }, [zoom.frame]);

  /**
   * Where a pointer landed, in the photo's natural pixels, plus how many of them
   * one screen pixel is worth — so a hit area meant to be comfortable under the
   * finger stays that size at any column width *and* at any zoom.
   */
  function pointerPixels(event: ReactPointerEvent) {
    const point = zoom.toContent(event.clientX, event.clientY);
    const box = zoom.frameRef.current?.getBoundingClientRect();
    if (!point || !box || box.width === 0) return null;

    return {
      x: point.x * naturalWidth,
      y: point.y * naturalHeight,
      scale: naturalWidth / (box.width * zoom.scale),
    };
  }

  /**
   * A mark's middle and its half-extents, all in the photo's natural pixels —
   * which is where its four handles sit (see PhotoHoldCanvas, which draws them
   * at exactly these points).
   *
   * On a circle the two half-extents are the same number, its radius: width is
   * a fraction of the photo's width and height a fraction of its height, and
   * `circularHold` keeps those in step. On a hold that knows its own shape they
   * are its box.
   */
  function geometry(hold: Hold) {
    return {
      cx: hold.x * naturalWidth,
      cy: hold.y * naturalHeight,
      hx: (hold.width / 2) * naturalWidth,
      hy: (hold.height / 2) * naturalHeight,
      r: holdRadius(hold) * naturalHeight,
    };
  }

  /**
   * Whether the press landed on one of the resize handles. Handles are only
   * drawn — and so only answer — when a single circle is picked up: four per
   * circle across a whole selection would be a thicket, and each would be
   * pulling towards a different radius.
   */
  function onHandle(point: { x: number; y: number; scale: number }): boolean {
    if (onlySelected === null || !holds[onlySelected]) return false;
    // **A measured shape has no handles.** Its outline was traced from the
    // pixels of the wall, so resizing it can only move it off the hold it came
    // from — the same reason it does not drag. A circle drawn by hand keeps
    // both, and the two sit side by side on one photo.
    if (fixedGeometry(holds[onlySelected])) return false;

    const { cx, cy, hx, hy } = geometry(holds[onlySelected]);
    const tolerance = HANDLE_HIT_PX * point.scale;

    // On a small circle the handles sit only a few pixels out from the middle,
    // and their tolerance would swallow the middle along with them. A press
    // nearer the centre than the edge is a press on the circle, whatever the
    // handles claim — otherwise the smallest holds could only ever be resized,
    // never tapped again to step their size.
    if (Math.hypot(point.x - cx, point.y - cy) <= Math.min(hx, hy) / 2) return false;

    return HOLD_HANDLES.some(
      ({ dx, dy }) => Math.hypot(point.x - (cx + dx * hx), point.y - (cy + dy * hy)) <= tolerance
    );
  }

  /**
   * Whether the press is on this mark — anywhere on it, not just its edge, plus
   * a few screen pixels of slack, because a finger is wider than a crimp.
   *
   * A hold that knows its own shape is tested **against that shape**, so a
   * press in the corner of its box that the hold does not reach falls through
   * to whatever is underneath. A circle is tested as a circle, as it always was.
   */
  function holdHit(hold: Hold, point: { x: number; y: number }, tolerance: number): boolean {
    const outline = holdOutline(hold);
    if (outline) {
      const grown = grownOutline(outline, {
        x: tolerance / naturalWidth,
        y: tolerance / naturalHeight,
      });

      return pointInOutline({ x: point.x / naturalWidth, y: point.y / naturalHeight }, grown);
    }

    const { cx, cy, r } = geometry(hold);

    return Math.hypot(point.x - cx, point.y - cy) <= r + tolerance;
  }

  /**
   * The mark under the pointer, or -1. The **smallest** one wins, so a hold
   * drawn inside a bigger one can still be picked up — by the area it covers,
   * which is the one measure a circle and a shape can be compared with.
   */
  function holdAt(point: { x: number; y: number; scale: number }): number {
    return nearestIn(holds, point);
  }

  /** The untaken placeholder under the pointer, or -1. */
  function candidateAt(point: { x: number; y: number; scale: number }): number {
    return nearestIn(candidates ?? [], point);
  }

  // The smallest mark of a stack wins, which is the rule a photograph needs:
  // a chip drawn inside a jug can still be picked up.
  function nearestIn(marks: Hold[], point: { x: number; y: number; scale: number }): number {
    const tolerance = HOLD_HIT_PX * point.scale;
    let best = -1;
    let bestArea = Infinity;

    marks.forEach((hold, index) => {
      const area = holdArea(hold);
      if (area <= bestArea && holdHit(hold, point, tolerance)) {
        best = index;
        bestArea = area;
      }
    });

    return best;
  }

  function handlePointerDown(event: ReactPointerEvent) {
    activePointers.current.add(event.pointerId);

    // A second finger down is the picture being moved or pinched, not a mark
    // being made: it takes back the circle the first finger was about to place
    // and abandons whatever it had hold of.
    if (activePointers.current.size > 1) {
      multiTouchRef.current = true;
      pendingRef.current = null;
      dragRef.current = null;
      return;
    }

    const point = pointerPixels(event);
    if (!point) return;

    // A fresh gesture: whatever the last one was, this one is one finger so far.
    multiTouchRef.current = false;
    pendingRef.current = null;
    zoom.frameRef.current?.setPointerCapture?.(event.pointerId);
    const pressed = { x: point.x / naturalWidth, y: point.y / naturalHeight };
    // Cmd on a Mac, Ctrl elsewhere: the platform's own "and this one too".
    const adding = event.metaKey || event.ctrlKey;

    // **An area being drawn, not holds being gathered** — whatever the press
    // landed on. See `onSweepArea`.
    if (onSweepArea) {
      if (pressed.x < 0 || pressed.x > 1 || pressed.y < 0 || pressed.y > 1) return;

      zoom.cancelPan();
      bandRef.current = { x: pressed.x, y: pressed.y, base: [] };
      setBand({ x1: pressed.x, y1: pressed.y, x2: pressed.x, y2: pressed.y });
      return;
    }

    // A handle belongs to the circle already picked up, so it is asked first —
    // the handles sit on its edge, where the circle itself would answer.
    if (onlySelected !== null && onHandle(point)) {
      // This press is resizing, not moving the picture.
      zoom.cancelPan();
      dragRef.current = {
        indices: [onlySelected],
        pressed: onlySelected,
        wasOnlySelection: false,
        mode: 'size',
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        grabOffset: pressed,
        from: [],
      };
      return;
    }

    const pressedHold = holdAt(point);
    if (pressedHold >= 0) {
      const alreadyHeld = selected.includes(pressedHold);

      // Where a press means membership, a plain one switches the hold into the
      // climb or out of it, and ⌘ picks up what is already in — see
      // `activation`. Neither ever moves anything.
      if (activation) {
        const active = activation.active.includes(pressedHold);

        if (adding) {
          // Nothing to pick up that is not in the climb yet: a group edit acts
          // on the climb, and a placeholder is not part of one.
          if (!active) return;

          return setSelected(
            alreadyHeld ? selected.filter((index) => index !== pressedHold) : [ ...selected, pressedHold ]
          );
        }

        if (active) {
          activation.onChange(activation.active.filter((index) => index !== pressedHold));
          return setSelected(selected.filter((index) => index !== pressedHold));
        }

        activation.onChange([ ...activation.active, pressedHold ]);
        return setSelected([ pressedHold ]);
      }

      if (adding) {
        // Toggling: the press adds this circle to what is held, or lets it go
        // again — and never starts a drag, which would move what was just
        // being counted in.
        setSelected(
          alreadyHeld ? selected.filter((index) => index !== pressedHold) : [...selected, pressedHold]
        );
        return;
      }

      // Pressing one of several already held keeps them all and moves them
      // together; pressing any other circle makes it the whole selection.
      const moving = alreadyHeld ? selected : [pressedHold];
      setSelected(moving);

      // **A measured outline is not yours to move, and that is decided by the
      // mark rather than by the screen.** Its shape was traced from the pixels
      // of the wall, so a thumb dragging it can only move it off the hold it
      // came from. A circle somebody drew by hand has no measurement behind it
      // and still moves — and the two sit side by side on the same photo,
      // which is why this is asked of the hold under the finger.
      // It is a mode rather than an early return, because the press still has
      // to reach the pointer lifting: a second press on the mark already in
      // hand is what cycles its role, and that is not a geometry gesture.
      const stays = fixedGeometry(holds[pressedHold]);

      // Dragging from here moves the circles, not the picture under them.
      zoom.cancelPan();
      dragRef.current = {
        indices: moving,
        pressed: pressedHold,
        wasOnlySelection: selected.length === 1 && alreadyHeld,
        mode: stays ? 'pinned' : 'move',
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        grabOffset: pressed,
        from: moving.map((index) => ({ x: holds[index].x, y: holds[index].y })),
      };
      return;
    }

    // A press on the wall with Cmd held is reaching for a circle and missing;
    // dropping a new one there would be a surprise, and clearing the selection
    // an annoyance, so it does nothing.
    if (adding) return;

    // Nor is a press that landed off the picture. The content is kept covering
    // the frame, so this is unreachable by gesture — it is here because nothing
    // downstream of a circle expects one outside the photo it is drawn on.
    if (pressed.x < 0 || pressed.x > 1 || pressed.y < 0 || pressed.y > 1) return;

    // **The climb's own holds were asked first and said no.** What is left
    // under the pointer may be a placeholder nobody has taken — pressing one
    // is how a hold joins the climb being edited.
    const candidate = candidateAt(point);
    if (candidate >= 0) {
      const mark = (candidates ?? [])[candidate];

      // The page's to say what that means, where it says.
      if (onTakeCandidate) {
        zoom.cancelPan();
        onTakeCandidate(mark, candidate);
        return;
      }

      // Otherwise it is a press on bare wall that has somewhere to go: the
      // placeholder goes down when the finger lifts, the way a circle would.
      // The pan the zoom armed is left armed, as it is for a circle — a drag
      // from here is still how the picture is moved about, and takes nothing.
      pendingRef.current = { ...pressed, startX: event.clientX, startY: event.clientY, mark };
      return;
    }

    // **A sweep across the wall takes everything it covers.** On a scanned wall
    // this is the gesture that replaces drawing: the holds are all there
    // already, so a drag is for gathering them rather than for making one more.
    if (marquee) {
      zoom.cancelPan();
      bandRef.current = { x: pressed.x, y: pressed.y, base: adding ? selected : [] };
      setBand({ x1: pressed.x, y1: pressed.y, x2: pressed.x, y2: pressed.y });
      return;
    }

    // **Nothing to draw on a wall that has already been looked at.** Where
    // every hold is a placeholder the detector found, a press on bare wall is a
    // press on wall — the climb is made by choosing holds, not by adding
    // circles. The pan the zoom armed stays armed, so the gesture is still how
    // the picture is moved about.
    if (!drawing) return;

    // Where the circle will go, once the finger lifts and the gesture has turned
    // out to have been a tap on the wall after all. The pan the zoom armed is
    // left armed: on bare wall, a drag is how the picture is moved about.
    pendingRef.current = { ...pressed, startX: event.clientX, startY: event.clientY };
  }

  /**
   * Put down the circle the press was holding. At the point the finger landed
   * rather than where it left: placing is placing, and a press that wandered on
   * the way was still aimed at the hold it started on — sizing is what the
   * handles are for, once the circle is on the wall.
   */
  /**
   * Every mark the swept rectangle covers, by index.
   *
   * **Touched, not enclosed.** A sweep that has to contain a hold entirely
   * makes the setter draw carefully around the edges of the wall; one that
   * takes whatever it crosses is the gesture people expect from every other
   * canvas. The test is against the mark's own shape rather than its box, so a
   * band passing through the gap beside a sloper does not take it.
   */
  function holdsWithin(x1: number, y1: number, x2: number, y2: number): number[] {
    const left = Math.min(x1, x2);
    const right = Math.max(x1, x2);
    const top = Math.min(y1, y2);
    const bottom = Math.max(y1, y2);

    return holds.reduce<number[]>((found, hold, index) => {
      const box = {
        left: hold.x - hold.width / 2,
        right: hold.x + hold.width / 2,
        top: hold.y - hold.height / 2,
        bottom: hold.y + hold.height / 2,
      };
      // Boxes first: a shape whose box misses the band cannot touch it, and
      // that is almost every hold on the wall.
      if (box.right < left || box.left > right || box.bottom < top || box.top > bottom) return found;

      const outline = holdOutline(hold);
      if (!outline) {
        found.push(index);
        return found;
      }

      // The band covers a point of the shape, or the shape covers a corner of
      // the band — which is what catches a sweep that began inside a big hold.
      const covered =
        outline.some(([x, y]) => x >= left && x <= right && y >= top && y <= bottom) ||
        [
          { x: left, y: top },
          { x: right, y: top },
          { x: right, y: bottom },
          { x: left, y: bottom },
        ].some((corner) => pointInOutline(corner, outline));
      if (covered) found.push(index);

      return found;
    }, []);
  }

  function placePending() {
    const pressed = pendingRef.current;
    pendingRef.current = null;
    if (!pressed) return;

    remember(holds);
    const color = PHOTO_HOLD_ROLE_COLORS[freeRole(holds, activeRole)];
    // A placeholder keeps the shape the scan found and takes on the role; a
    // press on bare wall gets a circle the size of the last one sized.
    const placed = pressed.mark
      ? { ...pressed.mark, color }
      : circularHold(pressed.x, pressed.y, radius, color, naturalWidth, naturalHeight);
    // Where it landed, not where it was put: on a circuit a start drawn last is
    // still the first hold of the run, and the panel has to stand at it there.
    const next = commit([...holds, placed]);
    setSelected([next.indexOf(placed)]);
  }

  function handlePointerMove(event: ReactPointerEvent) {
    const pending = pendingRef.current;
    // A press on bare wall that travels while the photo is enlarged was the
    // picture being dragged, not a circle being placed — and a drag that ends
    // somewhere else must not leave one behind where it started. Only while it
    // is enlarged: at rest there is nothing to drag the picture *to*, so the
    // gesture has one possible meaning and placing is placing.
    if (pending && zoom.zoomed) {
      const travelled = Math.hypot(event.clientX - pending.startX, event.clientY - pending.startY);
      if (travelled > DRAG_THRESHOLD_PX) pendingRef.current = null;
    }

    // A sweep in progress follows the pointer, and settles what it covers only
    // when the pointer lifts: a band redrawn forty times a second must not
    // rewrite the drawing forty times with it.
    const sweep = bandRef.current;
    if (sweep) {
      const at = pointerPixels(event);
      if (!at) return;

      setBand({
        x1: sweep.x,
        y1: sweep.y,
        x2: at.x / naturalWidth,
        y2: at.y / naturalHeight,
      });
      return;
    }

    const drag = dragRef.current;
    if (!drag) return;

    // Until the press has travelled it is still a press, not a drag — otherwise
    // the smallest tremor would nudge or resize what was only being selected.
    const travelled = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    if (!drag.moved && travelled < DRAG_THRESHOLD_PX) return;
    drag.moved = true;

    const point = pointerPixels(event);
    if (!point) return;

    // Where the circles stood when the drag began, recorded once — undoing a
    // move puts them back there rather than one pixel behind the pointer.
    if (!drag.remembered) {
      drag.remembered = true;
      remember(holds);
    }

    // A pinned mark goes nowhere, however far the pointer travels.
    if (drag.mode === 'pinned') return;

    if (drag.mode === 'size') {
      const hold = holds[drag.indices[0]];
      if (!hold) return;

      // A handle is dragged to where the edge should be, so how far out the
      // pointer is from the centre is the radius.
      const { cx, cy } = geometry(hold);
      // The drag put its starting state on the stack already; each frame of it
      // must not add another.
      setRadiusOf(drag.indices, clampRadius(Math.hypot(point.x - cx, point.y - cy) / naturalHeight), false);
      return;
    }

    // How far the press has travelled since it grabbed, applied to where each
    // circle was then — so a group keeps its shape and nothing accumulates.
    const delta = {
      x: point.x / naturalWidth - drag.grabOffset.x,
      y: point.y / naturalHeight - drag.grabOffset.y,
    };

    commit(
      holds.map((hold, index) => {
        const held = drag.indices.indexOf(index);
        if (held < 0) return hold;

        // Moved, not redrawn: whatever else the circle says about itself — the
        // hand it is taken with, and the swap that hand may be — goes with it.
        // Its shape does not: a measured outline belongs to the hold it was
        // measured on, and dragging it somewhere else makes it a circle.
        return circleOf(
          hold,
          drag.from[held].x + delta.x,
          drag.from[held].y + delta.y,
          holdRadius(hold)
        );
      })
    );
  }

  /**
   * The browser has taken the gesture over — the page is scrolling under the
   * finger. Nothing about it was a mark being made, so everything it had in hand
   * is dropped: reading down a page with a finger that happened to start on the
   * photo must not leave a circle behind on the way past.
   */
  function abandonGesture(event: ReactPointerEvent) {
    dragRef.current = null;
    pendingRef.current = null;
    activePointers.current.delete(event.pointerId);
    if (activePointers.current.size === 0) multiTouchRef.current = false;
    zoom.frameRef.current?.releasePointerCapture?.(event.pointerId);
  }

  function endDrag(event: ReactPointerEvent) {
    const sweep = bandRef.current;
    if (sweep && onSweepArea) {
      bandRef.current = null;
      const at = pointerPixels(event);
      setBand(null);
      activePointers.current.delete(event.pointerId);
      zoom.frameRef.current?.releasePointerCapture?.(event.pointerId);
      if (!at) return;

      const clamp = (value: number) => Math.min(1, Math.max(0, value));
      const x1 = clamp(Math.min(sweep.x, at.x / naturalWidth));
      const x2 = clamp(Math.max(sweep.x, at.x / naturalWidth));
      const y1 = clamp(Math.min(sweep.y, at.y / naturalHeight));
      const y2 = clamp(Math.max(sweep.y, at.y / naturalHeight));
      if (x2 - x1 < MIN_SWEPT_AREA || y2 - y1 < MIN_SWEPT_AREA) return;

      return onSweepArea({ x: x1, y: y1, width: x2 - x1, height: y2 - y1 });
    }
    if (sweep) {
      bandRef.current = null;
      const at = pointerPixels(event);
      const swept = at
        ? holdsWithin(sweep.x, sweep.y, at.x / naturalWidth, at.y / naturalHeight)
        : [];
      setBand(null);
      activePointers.current.delete(event.pointerId);
      zoom.frameRef.current?.releasePointerCapture?.(event.pointerId);

      // Where a press means membership, so does a sweep: it gathers everything
      // it crossed into the climb rather than picking it up.
      if (activation) {
        if (swept.length === 0) return setSelected([]);

        return activation.onChange(Array.from(new Set([ ...activation.active, ...swept ])));
      }

      // A sweep that covered nothing is how a selection is put down, which is
      // what a press on bare wall means everywhere else too.
      return setSelected(Array.from(new Set([...sweep.base, ...swept])));
    }

    const drag = dragRef.current;
    dragRef.current = null;
    activePointers.current.delete(event.pointerId);
    zoom.frameRef.current?.releasePointerCapture?.(event.pointerId);

    const wasMultiTouch = multiTouchRef.current;
    // The next gesture starts fresh once every finger is off the photo.
    if (activePointers.current.size === 0) multiTouchRef.current = false;

    // The gesture stayed one finger, so the press on the bare wall meant what it
    // looked like.
    if (wasMultiTouch) pendingRef.current = null;
    else placePending();

    // Only a press that stayed put means anything beyond what it already did.
    // A pinned mark counts: it refuses to be *moved*, not to be pressed, and
    // the second press on the one in hand is what cycles its role.
    if (!drag || drag.mode === 'size' || drag.moved) return;

    // A press on a group that never travelled was not a drag but a choice: it
    // meant that circle, not the group it happened to belong to.
    if (drag.indices.length > 1) return setSelected([drag.pressed]);

    // Pressing the circle already chosen means the next thing to do to it. The
    // first press picks a circle up, every press after it changes it — which of
    // its two properties is the caller's to say.
    if (!drag.wasOnlySelection) return;
    if (repeatPressCycles === 'role') cycleRole(drag.pressed);
    else cycleSize(drag.pressed);
  }

  /** Move this circle on to the next role, in the legend's order. */
  function cycleRole(index: number) {
    const hold = holds[index];
    if (!hold) return;

    remember(holds);

    const next =
      PHOTO_HOLD_ROLE_CYCLE[
        (PHOTO_HOLD_ROLE_CYCLE.indexOf(photoHoldRole(hold)) + 1) % PHOTO_HOLD_ROLE_CYCLE.length
      ];

    // The legend follows, so the next circle drawn carries on from here rather
    // than from whatever was armed before.
    setActiveRole(next);
    commit(
      holds.map((candidate, i) =>
        i === index ? { ...candidate, color: PHOTO_HOLD_ROLE_COLORS[next] } : candidate
      )
    );
  }

  /**
   * Step this circle up the ladder of sizes, wrapping to the smallest past the
   * largest.
   *
   * The handles size a circle exactly; this is the same thing done with a
   * thumb, for the setter working down a wall where every second hold wants to
   * be a little bigger or smaller than the last. Like the handles, it leaves its
   * size behind for the next circle drawn.
   *
   * A circle sized by hand sits between two rungs, so the step is taken from the
   * one it is nearest — which is what a thumb means by "the next size up".
   *
   * Not a mark whose geometry is fixed: a measured outline stepped up the
   * ladder would come out of it as a circle, its shape gone for a press that
   * was only meant to pick it up again.
   */
  function cycleSize(index: number) {
    const hold = holds[index];
    if (!hold || fixedGeometry(hold)) return;

    const current = holdRadius(hold);
    const nearest = HOLD_SIZE_LADDER.reduce(
      (best, rung, rungIndex) =>
        Math.abs(rung - current) < Math.abs(HOLD_SIZE_LADDER[best] - current) ? rungIndex : best,
      0
    );

    setRadiusOf([index], HOLD_SIZE_LADDER[(nearest + 1) % HOLD_SIZE_LADDER.length]);
  }

  /**
   * Give every one of these circles the same radius, about their own centres,
   * and let the next one drawn open at that size — a wall's holds come in runs
   * of a similar size, so sizing one is usually sizing the next few too. It is
   * what the handles leave behind, and the only way a new circle gets its size.
   */
  function setRadiusOf(indices: number[], nextRadius: number, record = true) {
    setRadius(nextRadius);
    if (record) remember(holds);
    commit(
      holds.map((hold, index) =>
        indices.includes(index) ? circleOf(hold, hold.x, hold.y, nextRadius) : hold
      )
    );
  }

  /**
   * Why the circles in hand cannot take this role, or null when they can.
   *
   * Counted against everything *else* on the wall: a start that is already one
   * of the four is not a fifth, so re-picking the colour a circle is already
   * painted in is never refused.
   */
  function roleBlocked(role: PhotoHoldRole): 'limit' | null {
    const others = holds.filter((_hold, index) => !selected.includes(index));

    return photoRoleAtLimit(others, role) ? 'limit' : null;
  }

  /** The panel repaints everything picked up, and arms the next circle. */
  function selectRole(role: PhotoHoldRole) {
    if (roleBlocked(role)) return;

    setActiveRole(role);
    if (selected.length === 0) return;

    remember(holds);
    commit(
      holds.map((hold, index) =>
        selected.includes(index) ? { ...hold, color: PHOTO_HOLD_ROLE_COLORS[role] } : hold
      )
    );
  }

  function deleteSelected() {
    if (selected.length === 0) return;

    remember(holds);
    onChange(holds.filter((_, index) => !selected.includes(index)));
    clearSelection();
  }

  /**
   * Move the circle in hand one place along the run — earlier with -1, later
   * with 1.
   *
   * The other way of reordering a circuit, for the setter who is already at the
   * circle: the list beside the photo drags a row to wherever it belongs, and
   * this steps the one in hand past its neighbour without looking away from the
   * wall. Only among equals — a hold climbed through cannot take the place of a
   * start or of the top (see `canMovePhotoStep`).
   */
  function moveSelectedStep(delta: -1 | 1) {
    if (onlySelected === null) return;

    // Where it is going, asked before the move so the panel can follow it there:
    // the two circles trade places, so the neighbour's is the one it takes.
    const landing = photoStepNeighbour(holds, onlySelected, delta);
    if (landing === null) return;

    remember(holds);
    commit(movePhotoStep(holds, onlySelected, delta));
    setSelected([landing]);
  }

  /**
   * Back one action — the last circle placed, the move, the repaint, or the
   * whole wall a clear took away. Stepping back through actions rather than
   * circles is what lets a clear be taken back at all.
   */
  function undo() {
    if (history.length === 0) return;

    const previous = history[history.length - 1];
    setHistory((past) => past.slice(0, -1));
    onChange(previous);
    clearSelection();
  }

  function clearAll() {
    if (holds.length === 0) return;

    remember(holds);
    onChange([]);
    clearSelection();
  }

  /**
   * Back to an empty wall: nothing in hand, start armed again, and nothing to
   * step back into.
   *
   * For the setter putting the next climb up on the same photo. The circle
   * *size* is deliberately kept — same wall, so the next problem's holds are
   * about the size of this one's — but everything that describes the drawing
   * just saved goes, the undo stack included: stepping back from a blank wall
   * must never bring the last climb's circles onto this one.
   */
  function reset() {
    setSelected([]);
    setActiveRole(INITIAL_PHOTO_HOLD_ROLE);
    setHistory([]);
  }

  /** The keyboard's way in, where Cmd is the pointer's: it adds rather than replaces. */
  function toggleSelected(index: number, adding: boolean) {
    setSelected((current) =>
      adding
        ? current.includes(index)
          ? current.filter((held) => held !== index)
          : [...current, index]
        : [index]
    );
  }

  // What stands between a drawing and a climb that can be saved.
  const missingRoles = useMemo(() => missingPhotoHoldRoles(holds), [holds]);

  /**
   * The number each circle wears on a circuit, aligned with the holds — null
   * where a circle has none: a foot is stood on throughout rather than moved to,
   * so the run passes over it. Empty on an ordinary climb, whose holds are a set
   * with no order to show.
   */
  const stepNumbers = useMemo<(string | null)[]>(
    () => (circuit ? photoHoldNumbers(holds) : []),
    [circuit, holds]
  );

  return {
    zoom,
    holds,
    naturalWidth,
    naturalHeight,
    activeRole,
    selectRole,
    roleBlocked,
    selected,
    onlySelected,
    selectedHold: onlySelected !== null ? (holds[onlySelected] ?? null) : null,
    anchorHold,
    /** What the panel stands at: the role of the circle it hangs off. */
    anchorRole: anchorHold ? photoHoldRole(anchorHold) : activeRole,
    /** And how big that circle is, in fractions of the photo's height. */
    anchorRadius: anchorHold ? holdRadius(anchorHold) : radius,
    /** The rungs both the panel's bar and a repeated press step through. */
    sizeLadder: HOLD_SIZE_LADDER,
    /**
     * Size everything picked up, and leave that size behind for the next
     * circle — every mark of it whose geometry is anybody's to change.
     */
    resizeSelected: (next: number) =>
      setRadiusOf(
        selected.filter((index) => !fixedGeometry(holds[index])),
        clampRadius(next)
      ),
    /**
     * Whether the mark in hand wears the four handles: one picked up on its
     * own, whose size can be changed at all. A measured outline has none, and
     * neither has anything on a pinned wall.
     */
    resizable: onlySelected !== null && !!holds[onlySelected] && !fixedGeometry(holds[onlySelected]),
    toggleSelected,
    /** The wall's untaken holds, for the canvas to draw. */
    candidates,
    /**
     * The rectangle being swept right now, in fractions of the photo, or null.
     * The canvas draws it; nothing else needs it.
     */
    band,
    clearSelection,
    deleteSelected,
    undo,
    canUndo: history.length > 0,
    clearAll,
    reset,
    missingRoles,
    circuit,
    stepNumbers,
    /** Where the circle in hand sits in the run, or null when it is no step of it. */
    selectedStep: onlySelected !== null ? (stepNumbers[onlySelected] ?? null) : null,
    /** Step the circle in hand one place along the run, and whether it may. */
    moveSelectedStep,
    canMoveSelectedStep: (delta: -1 | 1) =>
      onlySelected !== null && canMovePhotoStep(holds, onlySelected, delta),
    frameProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endDrag,
      onPointerCancel: abandonGesture,
    },
  };
}
